import { BUDGET_LABELS, TIMELINE_LABELS } from "./site";
import type {
  FieldErrors,
  InquiryPayload,
  InquiryResponse,
  ProjectsResponse,
} from "../types";

/**
 * In dev, Vite proxies /api to the Node server. In production, point
 * VITE_API_BASE_URL at the deployed API if it lives on another origin.
 * With no API URL set, a production build submits to Netlify Forms instead.
 */
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

const NETLIFY_FORM = "consultation";

const usesNetlifyForms =
  import.meta.env.PROD && !import.meta.env.VITE_API_BASE_URL;

/** Thrown for any non-2xx response; carries per-field errors when the API sent them. */
export class ApiError extends Error {
  readonly status: number;
  readonly fieldErrors: FieldErrors;

  constructor(message: string, status: number, fieldErrors: FieldErrors = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.fieldErrors = fieldErrors;
  }
}

const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
  let response: Response;

  try {
    response = await fetch(`${API_BASE}${path}`, {
      headers: { "Content-Type": "application/json" },
      ...init,
    });
  } catch {
    throw new ApiError(
      "Couldn't reach the server. Check your connection and try again.",
      0,
    );
  }

  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const payload = (body ?? {}) as { message?: string; fieldErrors?: FieldErrors };
    throw new ApiError(
      payload.message ?? `Request failed (${response.status}).`,
      response.status,
      payload.fieldErrors ?? {},
    );
  }

  return body as T;
};

export const fetchProjects = (category?: string): Promise<ProjectsResponse> => {
  const query =
    category && category !== "All"
      ? `?category=${encodeURIComponent(category)}`
      : "";
  return request<ProjectsResponse>(`/api/projects${query}`);
};

const submitToApi = (payload: InquiryPayload): Promise<InquiryResponse> =>
  request<InquiryResponse>("/api/contact", {
    method: "POST",
    body: JSON.stringify(payload),
  });

/** Posts the brief to the static form Netlify detected at deploy time. */
const submitToNetlify = async (
  payload: InquiryPayload,
): Promise<InquiryResponse> => {
  const received: InquiryResponse = {
    id: null,
    message: "Thanks — we'll be in touch within one business day.",
  };

  // Same honeypot contract as the API: accept it, store nothing.
  if (payload.website) return received;

  const body = new URLSearchParams({
    "form-name": NETLIFY_FORM,
    name: payload.name,
    email: payload.email,
    company: payload.company,
    projectType: payload.projectType,
    disciplines: payload.disciplines.join(", "),
    budget: BUDGET_LABELS[payload.budget] ?? payload.budget,
    timeline: TIMELINE_LABELS[payload.timeline] ?? payload.timeline,
    message: payload.message,
  });

  let response: Response;

  try {
    response = await fetch("/consultation.html", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
  } catch {
    throw new ApiError(
      "Couldn't reach the server. Check your connection and try again.",
      0,
    );
  }

  if (!response.ok) {
    throw new ApiError(
      "We couldn't send your request. Email us directly and we'll take it from there.",
      response.status,
    );
  }

  return received;
};

export const submitInquiry = (payload: InquiryPayload): Promise<InquiryResponse> =>
  usesNetlifyForms ? submitToNetlify(payload) : submitToApi(payload);
